(ns arthur.events.export
  "Export, as intents and one effect.

  The walk is not an event and must not become one: it is a promise chain that
  runs for as long as the symbol is long, and re-frame events are the wrong unit
  for something with a middle. So `::start` collects what the render needs out of
  the db and hands it to an fx, and the fx dispatches progress back — the same
  arrangement `events/project`'s save uses, and for the same reason.

  WHAT GOES IN THE DB IS THE REQUEST AND THE PROGRESS, never the frames. A
  megabyte of PNG in app-db would be compared by every mounted subscription on
  every tick."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.palette :as pal]
            [arthur.export :as export]
            [arthur.export.frames :as frames]
            [arthur.footage.store :as store]
            [clojure.string :as str]
            [re-frame.core :as rf]))

(def zooms
  "The integer zooms offered. 320x200 times these is 320x200 up to 1920x1200.

  INTEGERS ONLY, and the list is short for that reason rather than for tidiness:
  a stage at a non-integer scale has to invent pixels, and there is nothing in
  this tool downstream of `domain/raster` that is allowed to. 6 is here because
  1920 wide is what a delivery timeline usually is; the 1200 height that comes
  with it is 16:10 and is the project's aspect, not a mistake to letterbox away."
  [1 2 3 4 6])

(defn- stem
  "A filesystem-safe name for the artefact: the clip's label and the target's.

  The target is in the name because the clip, each symbol in its library and each
  placement on its stage are all exportable, and they would otherwise land in the
  downloads folder as the same file. It is the target's LABEL rather than its id
  because a placement's id is a uuid, and `arthur-8f594d72-a97f-....zip` names
  nothing to the person who has to find it again."
  [label target]
  (-> (str (or label "arthur") "-" (or target "main"))
      (str/replace #"[^A-Za-z0-9._-]+" "-")
      (str/replace #"^-+|-+$" "")
      (str/lower-case)))

(defn target-value
  "An export target as a `<select>` option value.

  Two kinds, told apart by a leading letter: `s:<symbol>` is a whole symbol,
  `n:<symbol>:<node>` is one instance inside one. The parts are joined with `:`
  because neither a symbol id nor a uuid contains one.

  IT CARRIES THE NAMESPACE. `(name :sym/face-8625)` is \"face-8625\", and a value
  written that way cannot be read back: `keyword` on it gives `:face-8625`, which
  is not a key in `:symbols`, so the plan silently becomes nil and the export
  throws \"there is no such symbol\" from inside re-frame's `:do-fx`. That
  presented as the tab locking up rather than as an error — see `::run!` below for
  the other half of why — and it is the reason this is a named pair of functions
  with a test rather than `name` and `keyword` at the two ends of a select."
  [{sid :symbol isolate :isolate}]
  (let [s (subs (str sid) 1)]
    (if isolate (str "n:" s ":" isolate) (str "s:" s))))

(defn target-id
  "The inverse of `target-value`. `keyword` splits on the `/` itself, so a
  namespaced symbol id survives; an instance comes back a uuid, which is what
  the node map is keyed by."
  [v]
  (let [[kind s node] (str/split v #":")]
    (cond-> {:symbol (keyword s)}
      (= "n" kind) (assoc :isolate (uuid node)))))

(defn targets
  "Everything an export can be pointed at, in the order the picker lists them.

  TWO KINDS, and the distinction is the point. A symbol is the DRAWING — one file
  however many times it is placed, in its own frame space. An instance is that
  drawing WHERE IT SITS in the open symbol: that symbol's length and rate, with
  the other instances removed, which is why seven instances of one symbol are
  seven different exports rather than seven copies of one.

  Instances are labelled by `clip/node-label` — the symbol they place — and
  numbered in stacking order where one symbol is placed more than once, never
  by id: a uuid sorts at random and means nothing to read."
  [clip open]
  (let [label #(clip/node-label clip %1 %2)
        instances (->> (get-in clip [:symbols open :nodes])
                       (filter (comp #{:instance} :kind val))
                       (sort-by (fn [[id n]] [(label id n) (or (:z n) "") (str id)])))
        numbered (mapcat (fn [same]
                           (if (< 1 (count same))
                             (map-indexed (fn [i [id n]] [id (str (label id n) " " (inc i))]) same)
                             (map (fn [[id n]] [id (label id n)]) same)))
                         (partition-by (fn [[id n]] (label id n)) instances))]
    (into (mapv (fn [sid] {:symbol sid :label (name sid)})
                (sort-by str (keys (:symbols clip))))
          (mapv (fn [[id l]] {:symbol open :isolate id :label l}) numbered))))

(defn target
  "What the export is pointed at. A nil symbol is whichever one is open, so a
  new document exports what is on screen without anyone choosing."
  [db]
  (let [{sid :symbol isolate :isolate} (:export db)]
    {:symbol (or sid (get-in db [:ui :open])) :isolate isolate}))

(defn- label-of
  "The label of the target `db` currently points at, for the filename."
  [clip open {sid :symbol isolate :isolate}]
  (:label (or (first (filter #(and (= sid (:symbol %)) (= isolate (:isolate %)))
                             (targets clip open)))
              {:label (some-> sid name)})))

(rf/reg-sub ::state (fn [db _] (assoc (:export db) :target (target db))))

(rf/reg-sub
 ::targets
 (fn [db _]
   (targets (:clip (store/entry (:clip/current db))) (get-in db [:ui :open]))))

(rf/reg-sub
 ::plan
 (fn [db _]
   (let [{:keys [clip]} (store/entry (:clip/current db))
         {sid :symbol isolate :isolate} (target db)]
     (export/plan {:clip clip :symbol sid :zoom (get-in db [:export :zoom])
                   :isolate isolate}))))

(rf/reg-event-db
 ::set-target
 ;; Both keys always, so switching from an instance back to a whole symbol
 ;; clears the isolate rather than leaving it to filter the new target.
 (fn [db [_ {sid :symbol isolate :isolate}]]
   (update db :export merge {:symbol sid :isolate isolate})))

(rf/reg-event-db
 ::set-zoom
 (fn [db [_ z]] (assoc-in db [:export :zoom] z)))

(rf/reg-event-fx
 ::start
 (fn [{:keys [db]} _]
   (if (get-in db [:export :busy?])
     {}
     (let [id    (:clip/current db)
           entry (store/entry id)
           {sid :symbol isolate :isolate} (target db)
           zoom  (get-in db [:export :zoom])]
       {:db (update db :export merge {:busy? true :done 0
                                      :total (:frames (export/plan
                                                       {:clip (:clip entry)
                                                        :symbol sid
                                                        :isolate isolate
                                                        :zoom zoom}))
                                      :status "rendering…"})
        ::run! {:clip        (:clip entry)
                :symbol      sid
                :isolate     isolate
                :store       (:store entry)
                ;; The same palette and ramp the preview resolves and blits
                ;; through. Read here rather than in the fx so that the effect
                ;; takes data and nothing else.
                :palette     (pal/compile (:clip entry))
                :ramp        (let [p (pal/compile (:clip entry))] (pal/effective-ramp p (:default p)))
                :zoom        zoom
                :audio-url   (:audio entry)
                :name        (stem (:label entry)
                                   (label-of (:clip entry) (get-in db [:ui :open])
                                             {:symbol sid :isolate isolate}))}}))))

(rf/reg-event-db
 ::progress
 (fn [db [_ done total]]
   (update db :export merge {:done done :total total})))

(rf/reg-event-db
 ::done
 (fn [db [_ filename bytes]]
   (update db :export merge
           {:busy? false
            :status (str "wrote " filename " · "
                         (.toFixed (/ bytes 1048576) 1) " MB")})))

(rf/reg-event-db
 ::failed
 (fn [db [_ message]]
   (update db :export merge {:busy? false :status (str "export failed: " message)})))

(defn- download!
  "Hand the browser a blob as a file.

  The object URL is revoked on a timeout rather than immediately: the click starts
  the download asynchronously and revoking in the same turn cancels it in some
  browsers, which presents as the button doing nothing at all."
  [filename ^js blob]
  (let [url (js/URL.createObjectURL blob)
        a   (.createElement js/document "a")]
    (set! (.-href a) url)
    (set! (.-download a) filename)
    (.appendChild (.-body js/document) a)
    (.click a)
    (.removeChild (.-body js/document) a)
    (js/setTimeout #(js/URL.revokeObjectURL url) 30000)))

(rf/reg-fx
 ::run!
 (fn [spec]
   ;; THE CALL IS GUARDED because `export/run!` validates its request BEFORE it
   ;; returns a promise, so a bad symbol id throws synchronously — here, inside
   ;; re-frame's `:do-fx` interceptor. An uncaught throw there never reaches the
   ;; `.catch` below, so `::failed` never dispatches and `:busy?` stays true: the
   ;; button sits disabled on \"rendering…\" and the readout on \"frame 0 /\"
   ;; forever. That reads as the tab having locked up, which is the worst way for
   ;; an export to fail — there is nothing to see and nothing in the status line.
   ;; Turning the throw into a rejection gives every failure one path to the user.
   (-> (try (export/run! spec
                         (frames/exporter)
                         (fn [done total] (rf/dispatch [::progress done total])))
            (catch :default e (js/Promise.reject e)))
       (.then (fn [{:keys [filename ^js blob]}]
                (download! filename blob)
                (rf/dispatch [::done filename (.-size blob)])))
       (.catch (fn [error]
                 (js/console.error error)
                 (rf/dispatch [::failed (or (ex-message error) (str error))]))))))
